import { Dispatch, SetStateAction, useEffect } from "react";
import {
  FormProvider,
  useForm,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Card, Col, Container, Row, Button } from "reactstrap";
import { H2, Btn } from "../../../../AbstractElements";
import { useAppDispatch } from "../../../../ReduxToolkit/Hooks";
import {
  AddFields,
  RemoveField,
  danger,
  primary,
  success,
  conveyances,
  user_id,
  car,
  bike,
  cab,
  auto,
} from "../../../../utils/Constant";
import { convertArrayToFormData } from "../../../../utils/formUtils";
import { conveyanceManagementvalidationSchema } from "../validationSchema";
import { conveyanceFormFields } from "../fields/conveyanceFormFields";
import { DynamicFieldRenderer } from "../../../../CommonElements/DynamicFieldRenderer";
import { useAddConveyanceMutation } from "../../../../ReduxToolkit/api/conveyance";
import { useEmployeeProfile } from "../../../../hooks/useEmployeeProfile";
import { showApiResultAlert } from "../../../../utils/AlertManager/showApiAlert";
import {
  IEmployeeModulesConfig,
  IRTKBaseResponse,
} from "../../../../Types/UserProfileDetails";
import { getConfigEligibleDateRange } from "../../../../utils/getConfigEligibleDate";
import IconComponent from "../../../../CommonElements/IconComponent";
import { useLocation } from "react-router-dom";
import { Nullable } from "../../../../Types/Common";
import { getLocalStorage } from "../../../../utils/localStorageHelpers";
import { canAddBackdatedExpenses, getExpenseMonthStart } from "../../../../utils/validation/expenseDateValidation";

const calculateAmount = (mode: string, distance: number) => {
  if (!mode || !distance) return 0;
  const normalizedMode = mode.toLowerCase();
  if (normalizedMode === bike) return distance * 4;
  if (normalizedMode === car) return distance * 7;
  return 0;
};

const isAutoCalculatedMode = (mode?: string) => {
  const normalizedMode = mode?.toLowerCase();
  return normalizedMode === car || normalizedMode === bike;
};

const isBillRequiredMode = (mode?: string) => {
  const normalizedMode = mode?.toLowerCase();
  return normalizedMode === cab || normalizedMode === auto;
};

const getDateOnly = (date: string | Date) => {
  const parsedDate = date instanceof Date ? new Date(date) : new Date(date);
  parsedDate.setHours(0, 0, 0, 0);
  return parsedDate;
};

const getTodayDateOnly = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return today;
};

const getYmdDate = (date: Date) => date.toLocaleDateString("en-CA");

const getEffectiveMinDate = (configuredMinDate?: string) => {
  const today = getTodayDateOnly();
  const monthStart = getExpenseMonthStart(today);
  const configuredDate = configuredMinDate
    ? getDateOnly(configuredMinDate)
    : monthStart;

  return getYmdDate(configuredDate > monthStart ? configuredDate : monthStart);
};

const getEffectiveMaxDate = (configuredMaxDate?: string) => {
  const today = getTodayDateOnly();

  if (!configuredMaxDate) return getYmdDate(today);

  const parsedConfiguredMaxDate = getDateOnly(configuredMaxDate);

  if (Number.isNaN(parsedConfiguredMaxDate.getTime())) {
    return getYmdDate(today);
  }

  return getYmdDate(
    parsedConfiguredMaxDate > today ? today : parsedConfiguredMaxDate
  );
};

interface ConveyanceFormField {
  date: string | Date;
  reason: string;
  source: string;
  destination: string;
  distance: string | number;
  mode: string;
  amount: string | number;
  bill: Nullable<File>;
}

interface ConveyanceForm {
  forms: ConveyanceFormField[];
}

const defaultValues = {
  forms: [
    {
      date: new Date().toISOString(),
      reason: "",
      source: "",
      destination: "",
      distance: "",
      mode: "",
      amount: "",
      bill: null,
    },
  ],
};

export interface IAddConveyanceManagementProps {
  isAddConveyanceManagement?: boolean;
  setIsAddConveyanceManagement?: Dispatch<SetStateAction<boolean>>;
}

const AddConveyanceManagement = ({
  isAddConveyanceManagement,
  setIsAddConveyanceManagement,
}: IAddConveyanceManagementProps) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loggedInEmployee, loggedInEmployeeRole, employeeProfileDetails } =
    useEmployeeProfile();
  const employeeModuleConfig = getLocalStorage<IEmployeeModulesConfig | null>(
    "userConfig",
    null,
  );
  const { minDate, maxDate, isBypassedRole } = getConfigEligibleDateRange(
    employeeModuleConfig,
    loggedInEmployeeRole
  );
  const canBackdate = canAddBackdatedExpenses(loggedInEmployeeRole);
  const effectiveMaxDate = getEffectiveMaxDate(canBackdate ? undefined : maxDate);
  const effectiveMinDate = canBackdate ? undefined : getEffectiveMinDate(minDate);

  const [
    addConveyanceEmployee,
    { isLoading, isError, error, isSuccess, reset: resetAddConveyanceEmployee },
  ] = useAddConveyanceMutation();

  const handleBackListConveyance = () => {
    if (setIsAddConveyanceManagement)
      setIsAddConveyanceManagement((prev) => !prev);
  };

  const methods = useForm<ConveyanceForm>({
    defaultValues,
    resolver: yupResolver(conveyanceManagementvalidationSchema) as any,
    context: { loggedInEmployeeRole },
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const officialAlertMeta = {
    title: `${loggedInEmployee?.name} Info`,
    source: "",
  };

  const { control, handleSubmit, setValue, reset, trigger } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: "forms" });

  const watchedForms = useWatch({ control, name: "forms" });

  useEffect(() => {
    watchedForms?.forEach((row, index) => {
      const distance = Number(row.distance);
      const mode = row.mode;
      if (!isAutoCalculatedMode(mode)) return;

      const calculated = calculateAmount(mode, distance);

      // update only if changed
      if (row.amount !== calculated && !isNaN(calculated)) {
        setValue(`forms.${index}.amount`, calculated, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });
  }, [watchedForms, setValue]);

  useEffect(() => {
    watchedForms?.forEach((row, index) => {
      const fieldsToValidate: Array<
        | `forms.${number}.distance`
        | `forms.${number}.amount`
        | `forms.${number}.bill`
      > = [];

      if (row.distance !== "" && row.distance !== null && row.distance !== undefined) {
        fieldsToValidate.push(`forms.${index}.distance`);
      }

      if (row.amount !== "" && row.amount !== null && row.amount !== undefined) {
        fieldsToValidate.push(`forms.${index}.amount`);
      }

      if (isBillRequiredMode(row.mode) || row.bill) {
        fieldsToValidate.push(`forms.${index}.bill`);
      }

      if (fieldsToValidate.length) {
        trigger(fieldsToValidate);
      }
    });
  }, [watchedForms, trigger]);

  const handleAddFields = () => append(defaultValues.forms);

  const handleReset = (response: IRTKBaseResponse) => {
    if (response.data?.success) {
      reset();
      methods.reset(defaultValues, { keepValues: false });
    }
  };

  const onSubmit = async (data: any) => {
    const formatted = data.forms.map((d: any) => ({
      date: new Date(d.date).toLocaleDateString("en-CA"),
      reason: d.reason,
      source: d.source,
      destination: d.destination,
      distance: Number(d.distance),
      amount: Number(d.amount),
      mode: d.mode,
    }));

    const formData = convertArrayToFormData(formatted, conveyances);
    data?.forms?.forEach((d: any, i: number) => {
      d?.bill?.map((e: any) => formData.append(`conveyances[${i}][bill]`, e));
    });
    formData.append(
      user_id,
      String(
        !location.pathname.includes("/employee-zone/add-conveyance")
          ? employeeProfileDetails?.jobInformation?.userId
          : loggedInEmployee?.id
      )
    ); // replace with dynamic user ID

    const addConveyanceResponse = await addConveyanceEmployee(formData);
    showApiResultAlert(
      addConveyanceResponse,
      officialAlertMeta,
      () => handleReset(addConveyanceResponse),
      dispatch,
      true
    );
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <Card
            className={`my-${
              !location.pathname.includes("/employee-zone/add-conveyance")
                ? "1"
                : "4"
            } py-4 px-3`}
          >
            <H2 className="mb-3">Add Conveyance</H2>

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} className="theme-form">
                <Row>
                  <Col md="2">
                    <Button
                      color={primary}
                      type="button"
                      className="mb-3 w-100"
                      onClick={handleAddFields}
                    >
                      {AddFields}
                    </Button>
                  </Col>

                  {!location.pathname.includes(
                    "/employee-zone/add-conveyance"
                  ) && (
                    <Col sm="4" xxl="3">
                      <Button
                        className="m-0 w-100"
                        color="secondary"
                        onClick={handleBackListConveyance}
                      >
                        Back To List Conveyance
                      </Button>
                    </Col>
                  )}
                </Row>

                {fields.map((item, index) => (
                  <div key={item.id} className="border b-r-5 p-3 mb-3">
                    <Row>
                      {conveyanceFormFields.map((field) => {
                        const currentMode = watchedForms?.[index]?.mode;
                        const extendedField =
                          field.name === "date"
                            ? {
                                ...field,
                                minDate: effectiveMinDate,
                                maxDate: effectiveMaxDate,
                                disabled: field.disabled ?? false,
                              }
                            : field;

                        return (
                          <Col
                            md={field.col ?? 3}
                            key={`${field.name}-${index}`}
                          >
                            {DynamicFieldRenderer(
                              {
                                ...extendedField,
                                name: `forms.${index}.${field.name}`,
                                disabled:
                                  field.name === "amount"
                                    ? isAutoCalculatedMode(currentMode)
                                    : extendedField.disabled,
                              },
                              field.options
                            )}
                          </Col>
                        );
                      })}

                      {index > 0 && (
                        <Col md="3" className="d-flex align-items-end">
                          <Btn
                            type="button"
                            color={danger}
                            onClick={() => remove(index)}
                          >
                            {RemoveField}
                          </Btn>
                        </Col>
                      )}
                    </Row>
                  </div>
                ))}

                <Btn
                  type="submit"
                  color={success}
                  className="mt-3"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <IconComponent className="fa fa-spin fa-spinner " />{" "}
                      Please wait....
                    </>
                  ) : (
                    "Submit"
                  )}
                </Btn>
              </form>
            </FormProvider>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AddConveyanceManagement;
